import { trackBrandsBrandTileClick } from "@faire/web-api/events/brands/click/brandTile";
import { trackCategoriesBrandBatchView } from "@faire/web-api/events/categories/view/brandBatch";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import isChromatic from "chromatic/isChromatic";
import { observer } from "mobx-react";
import * as React from "react";
import { useMemo, type JSX } from "react";
import styled, { css } from "styled-components";

import { EfficientListLoader } from "@faire/retailer/components/EfficientListLoader/EfficientListLoader";
import { withFallback } from "@faire/retailer/components/GhostLoading/helpers";
import { ILeanBrandTile } from "@faire/retailer/components/LeanBrandTileRenderer/ILeanBrandTileAdapters";
import { LeanBrandTileRenderer } from "@faire/retailer/components/LeanBrandTileRenderer/LeanBrandTileRenderer";
import { PAGE_TYPE } from "@faire/retailer/consts/PAGE_TYPE";
import {
  followBrand,
  trackBoardsEvent,
  unFollowBrand,
} from "@faire/retailer/features/RetailerBoards/utils";
import { SignUpElement } from "@faire/retailer/lib/popSignUp";
import { Category } from "@faire/retailer/models/Category/Category";

const BRAND_TILE_CONTAINER_CONTEXT = "CATEGORY_BRAND_RESULTS_GRID";
const ROW_PER_BATCH = 2;

interface IProps {
  searchResults: (ILeanBrandTile | undefined)[];
  category?: Category;
  filterKeys: string[];
  isLoading: boolean;
  scrollRef?: React.MutableRefObject<HTMLDivElement | null>;
  requestId?: string;
  title?: string | undefined;
  pageType?: PAGE_TYPE;
  isScrollingToTop?: boolean;
  onFirstBatchVisible?: () => void;
  itemsPerRow?: ResponsiveValues<number>;
}

const BrandGridBase: React.FC<IProps> = ({
  searchResults,
  requestId,
  title,
  pageType,
  category,
  filterKeys,
  isLoading,
  scrollRef,
  isScrollingToTop,
  onFirstBatchVisible,
  itemsPerRow: overrideItemsPerRow,
}) => {
  const isSSR = useIsSSR();

  const onBrandBatchVisible = (
    items: Array<ILeanBrandTile | undefined>,
    tileBatchIndex?: number
  ) => {
    if (requestId) {
      trackCategoriesBrandBatchView(
        requestId,
        items
          .map((i) => i?.brand?.token)
          .filter(isNotUndefined)
          .join(" ")
      );
    }

    if (tileBatchIndex === 0) {
      onFirstBatchVisible?.();
    }
  };

  const postFavoriteBrand = (brandToken: string, brandName: string) => {
    followBrand(brandToken, brandName);
    trackBoardsEvent(`brand/${brandToken}/follow/category`);
  };

  const deleteFavoriteBrand = (brandToken: string) => {
    unFollowBrand(brandToken);
    trackBoardsEvent(`brand/${brandToken}/unfollow/category`);
  };

  const handleBrandClick = (brandToken?: string) => {
    if (brandToken && category) {
      const eventContextString = `${BRAND_TILE_CONTAINER_CONTEXT}|${category.mostSpecificPathIdentifier}`;
      trackBrandsBrandTileClick(
        eventContextString,
        requestId ?? "",
        brandToken
      );
    }
  };

  const renderTile = (tileData: ILeanBrandTile | undefined) => (
    <TileWrapper key={tileData?.brand?.token} data-test-id="maker-tile-wrapper">
      <LeanBrandTileRenderer
        title={title}
        pageType={pageType}
        brandTile={tileData}
        categoryUrlIdList={category?.urlIdStrings}
        filterKeys={filterKeys}
        favoriteBrand={postFavoriteBrand}
        unFavoriteBrand={deleteFavoriteBrand}
        onClick={handleBrandClick}
        signUpTracking={{
          element: SignUpElement.PRODUCT_GRID,
          sourceId: "category_brand_grid",
        }}
      />
    </TileWrapper>
  );

  const renderPlaceHolder = (
    placeholderItems?: (ILeanBrandTile | undefined)[],
    tileBatchIndex?: number
  ): JSX.Element => {
    return (
      <React.Fragment key={`placeholder-batch-${tileBatchIndex}`}>
        {placeholderItems?.map((_, index) => (
          <React.Fragment key={`placeholder-${index}`}>
            {renderTile(undefined)}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  };

  const itemsPerRow = useResponsiveValue(
    overrideItemsPerRow ?? {
      mobileAndAbove: 2,
      tabletAndAbove: 4,
    }
  );

  const items = useMemo(() => {
    if (searchResults.length > 0) {
      return searchResults;
    }
    return withFallback(searchResults, 20);
  }, [searchResults]);

  const testIdAttribute = isLoading
    ? "marketplace-brand-list-loading"
    : "marketplace-brand-list";

  return (
    <Wrapper
      data-test-id={testIdAttribute}
      $columns={itemsPerRow}
      ref={scrollRef}
    >
      <EfficientListLoader
        data={items}
        batchSize={itemsPerRow * ROW_PER_BATCH}
        forceRender={isSSR || isChromatic()}
        // Remember to update the placeholder if the tile changes
        placeholderFn={renderPlaceHolder}
        renderComponent={renderTile}
        onBatchVisible={onBrandBatchVisible}
        hashKey={requestId}
        renderPlaceholderForNonFirstBatch={isScrollingToTop}
        visibilityWrapperDisplayProperty="unset"
      />
    </Wrapper>
  );
};

export const CategoryPageBrandGrid = observer(BrandGridBase);

const Wrapper = styled.div<{ $columns: number }>`
  display: grid;
  align-self: stretch;
  //minmax(0, 1fr) ensures text overflow happens properly
  grid-template-columns:
    [start] repeat(${(props) => props.$columns}, minmax(0, 1fr))
    [end];
  margin: 0 -4px;

  ${responsiveMediaQueries.tabletAndAbove} {
    margin: 0 -8px;
  }
`;

const TileWrapperStyles = css`
  width: 100%;
  padding: 8px 4px;

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 8px;
  }
`;

const TileWrapper = styled.div`
  ${TileWrapperStyles}
`;
